import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Optional error reporter (e.g. Sentry) */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Label for the boundary (shown in dev mode error panel) */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * ErrorBoundary — catches render-time errors and shows a friendly recovery UI
 * instead of a blank white screen.
 *
 * Usage:
 *   <ErrorBoundary label="ConsultationPage">
 *     <ConsultationPage />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Report to external monitoring if configured
    this.props.onError?.(error, info);

    // In development, log with component stack for easy debugging
    if (import.meta.env.DEV) {
      console.error(
        `[ErrorBoundary${this.props.label ? ` :: ${this.props.label}` : ""}] Caught error:`,
        error,
        info.componentStack,
      );
    }
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;

    if (error) {
      // Allow a custom fallback to be rendered
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <div
            style={{
              maxWidth: "480px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "0.75rem",
              padding: "2rem",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,.07)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "#fef2f2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
                fontSize: "1.5rem",
              }}
            >
              ⚠️
            </div>

            <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.125rem", fontWeight: 600 }}>
              Something went wrong
            </h2>

            <p style={{ margin: "0 0 1.5rem", fontSize: "0.875rem", color: "#6b7280" }}>
              An unexpected error occurred. Your data is safe. You can try reloading
              this section or refreshing the page.
            </p>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: "0.5rem 1.25rem",
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: "0.375rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "0.5rem 1.25rem",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Reload page
              </button>
            </div>

            {/* Show error details in development only */}
            {import.meta.env.DEV && (
              <details
                style={{
                  marginTop: "1.5rem",
                  textAlign: "left",
                  background: "#f9fafb",
                  padding: "1rem",
                  borderRadius: "0.375rem",
                  border: "1px solid #e5e7eb",
                }}
              >
                <summary
                  style={{ cursor: "pointer", fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}
                >
                  Error details (dev only)
                </summary>
                <pre
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.7rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#dc2626",
                  }}
                >
                  {error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
